import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { LawIcon, MenuIcon } from "./Icons.tsx";
import { ChatInput } from "./ChatInput.tsx";
import { LanguageSwitcher } from "./LanguageSwitcher.tsx";
import { BrandLogo } from "./BrandLogo.tsx";
import { QuickStartsGrid } from "./QuickStartsGrid.tsx";
import { REVOLVING_TEXT_KEYS } from "./SearchHeader.constants.tsx";

interface SearchHeaderProps {
  query: string;
  setQuery: (query: string) => void;
  handleSubmit: (e?: React.FormEvent, queryOverride?: string) => void;
  isSearched: boolean;
  isLoading: boolean;
  onMenuClick?: () => void;
  onLogoClick?: () => void;
  selectedFile?: File | null;
  setSelectedFile?: (file: File | null) => void;
}

export const SearchHeader: React.FC<SearchHeaderProps> = ({
  query,
  setQuery,
  handleSubmit,
  isSearched,
  isLoading,
  onMenuClick,
  onLogoClick,
  selectedFile,
  setSelectedFile,
}) => {
  const { t } = useTranslation();
  const [revolvingIndex, setRevolvingIndex] = useState(0);
  const [fade, setFade] = useState(true);

  useEffect(() => {
    if (isSearched) return;
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setRevolvingIndex(prev => (prev + 1) % REVOLVING_TEXT_KEYS.length);
        setFade(true);
      }, 500);
    }, 4000);
    return () => clearInterval(interval);
  }, [isSearched]);

  return (
    <header className={`transition-all duration-700 ease-in-out flex flex-col items-center justify-center w-full ${isSearched ? "border-b border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-950/80 backdrop-blur-xl sticky top-0 z-20" : "flex-1 relative py-12 px-4"}`}>

      {/* Header Top Bar */}
      <div className={`${isSearched ? "relative w-full" : "absolute top-0 left-0 right-0 pointer-events-none"} p-4 sm:px-8 sm:py-6 flex items-center justify-between z-30`}>
        <div className={`flex-1 flex justify-start items-center ${!isSearched ? "pointer-events-auto" : ""}`}>
          {onMenuClick && (
            <button
              onClick={onMenuClick}
              className="p-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-sm hover:shadow-md transition-all text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 touch-manipulation"
              title={t("common.open_sidebar", "Ava külgriba")}
              aria-label={t("common.open_sidebar", "Ava külgriba")}
            >
              <MenuIcon />
            </button>
          )}
        </div>

        {isSearched && (
          <BrandLogo
            onClick={onLogoClick}
            title={t("common.logo_title", "Õigusabi24.ee")}
            hideIconOnMobile={true}
            className="flex-none"
          />
        )}

        <div className={`flex-1 flex justify-end items-center ${!isSearched ? "pointer-events-auto" : ""}`}>
          <LanguageSwitcher />
        </div>
      </div>

      {/* Hero Section */}
      {!isSearched && (
        <div
          onClick={onLogoClick}
          className="flex flex-col items-center mb-10 scale-100 cursor-pointer group mt-8 sm:mt-0"
        >
          <div className="bg-blue-600 p-4 rounded-3xl text-white shadow-xl shadow-blue-500/20 animate-float mb-6 group-hover:scale-105 transition-transform">
            <LawIcon />
          </div>
          <div className="text-center w-full max-w-4xl mx-auto">
            <h1 className="text-4xl sm:text-6xl font-extrabold tracking-tight text-slate-900 dark:text-white drop-shadow-sm text-balance px-4 leading-[1.1]">
              {t("common.logo_title", "Õigusabi24.ee")}
            </h1>
            <p className={`text-lg sm:text-xl text-slate-500 dark:text-slate-400 mt-6 max-w-2xl mx-auto transition-opacity duration-500 ${fade ? "opacity-100" : "opacity-0"} text-balance px-4`}>
              {t(REVOLVING_TEXT_KEYS[revolvingIndex] || "")}
            </p>
          </div>
        </div>
      )}

      {!isSearched && (
        <div className="w-full max-w-3xl transition-all duration-700">
          <ChatInput
            query={query}
            setQuery={setQuery}
            handleSubmit={handleSubmit}
            isLoading={isLoading}
            isSearched={isSearched}
            selectedFile={selectedFile}
            setSelectedFile={setSelectedFile}
          />
          <QuickStartsGrid />
        </div>
      )}
    </header>
  );
};
